import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type CalendarView = "days" | "months" | "years";

interface CalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onClose?: () => void;
  maxDate?: Date;
  minDate?: Date;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const YEARS_PER_PAGE = 12;

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  maxDate,
  minDate,
}) => {
  const [view, setView] = useState<CalendarView>("days");
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
  );
  /* Start of the visible 12-year block */
  const [yearRangeStart, setYearRangeStart] = useState(
    Math.floor(selectedDate.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE
  );

  /* ── helpers ── */
  const clampDate = (date: Date): boolean => {
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    if (maxDate) { const mx = new Date(maxDate); mx.setHours(0,0,0,0); if (d > mx) return true; }
    if (minDate) { const mn = new Date(minDate); mn.setHours(0,0,0,0); if (d < mn) return true; }
    return false;
  };

  const isToday = (date: Date) => {
    const t = new Date(); t.setHours(0,0,0,0);
    const d = new Date(date); d.setHours(0,0,0,0);
    return d.getTime() === t.getTime();
  };

  const isSelected = (date: Date) => {
    const s = new Date(selectedDate); s.setHours(0,0,0,0);
    const d = new Date(date); d.setHours(0,0,0,0);
    return d.getTime() === s.getTime();
  };

  /* ── Days view logic ── */
  const getDaysInMonth = (date: Date) => {
    const y = date.getFullYear(), m = date.getMonth();
    return {
      daysInMonth: new Date(y, m + 1, 0).getDate(),
      startingDayOfWeek: new Date(y, m, 1).getDay(),
    };
  };

  const handleDaySelect = (day: number) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    d.setHours(0, 0, 0, 0);
    if (clampDate(d)) return;
    onDateSelect(d);
  };

  const canPrevMonth = () => {
    if (!minDate) return true;
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return prev >= new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  };
  const canNextMonth = () => {
    if (!maxDate) return true;
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return next <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  };

  /* ── Month view logic ── */
  const isMonthDisabled = (monthIndex: number) => {
    const y = currentMonth.getFullYear();
    if (maxDate && new Date(y, monthIndex + 1, 0) > maxDate) return false; // allow if any day valid
    if (minDate && new Date(y, monthIndex, 1) < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) return true;
    if (maxDate && new Date(y, monthIndex, 1) > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) return true;
    return false;
  };

  const handleMonthSelect = (monthIndex: number) => {
    if (isMonthDisabled(monthIndex)) return;
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
    setView("days");
  };

  const canPrevYear = () =>
    !minDate || currentMonth.getFullYear() - 1 >= minDate.getFullYear();
  const canNextYear = () =>
    !maxDate || currentMonth.getFullYear() + 1 <= maxDate.getFullYear();

  /* ── Year view logic ── */
  const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearRangeStart + i);

  const isYearDisabled = (year: number) => {
    if (maxDate && year > maxDate.getFullYear()) return true;
    if (minDate && year < minDate.getFullYear()) return true;
    return false;
  };

  const handleYearSelect = (year: number) => {
    if (isYearDisabled(year)) return;
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setView("months");
  };

  const canPrevYearRange = () =>
    !minDate || yearRangeStart - YEARS_PER_PAGE >= Math.floor(minDate.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE;
  const canNextYearRange = () =>
    !maxDate || yearRangeStart + YEARS_PER_PAGE <= maxDate.getFullYear();

  /* ── Renders ── */
  const renderDays = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < startingDayOfWeek; i++)
      cells.push(<div key={`e-${i}`} className="h-8" />);

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      date.setHours(0, 0, 0, 0);
      const disabled = clampDate(date);
      const selected = isSelected(date);
      const today = isToday(date);

      cells.push(
        <button
          key={day}
          type="button"
          onClick={() => !disabled && handleDaySelect(day)}
          disabled={disabled}
          className={`h-8 w-8 rounded-full text-sm flex items-center justify-center transition-colors mx-auto
            ${disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-gray-100"}
            ${selected ? "bg-[#00A9A9] text-white hover:bg-[#008F8F]" : ""}
            ${today && !selected ? "border border-[#00A9A9] text-[#00A9A9] font-semibold" : ""}
          `}
        >
          {day}
        </button>
      );
    }
    return cells;
  };

  const renderMonths = () =>
    MONTHS.map((name, i) => {
      const disabled = isMonthDisabled(i);
      const active = i === selectedDate.getMonth() && currentMonth.getFullYear() === selectedDate.getFullYear();
      const current = i === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear();
      return (
        <button
          key={name}
          type="button"
          onClick={() => handleMonthSelect(i)}
          disabled={disabled}
          className={`h-10 rounded-lg text-sm font-inter font-medium transition-colors
            ${disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-[#E5F8F8]"}
            ${active ? "bg-[#00A9A9] text-white hover:bg-[#008F8F]" : ""}
            ${current && !active ? "border border-[#00A9A9] text-[#00A9A9]" : ""}
            ${!active && !current && !disabled ? "text-[#1F1F1F]" : ""}
          `}
        >
          {name}
        </button>
      );
    });

  const renderYears = () =>
    years.map((year) => {
      const disabled = isYearDisabled(year);
      const active = year === selectedDate.getFullYear();
      const current = year === new Date().getFullYear();
      return (
        <button
          key={year}
          type="button"
          onClick={() => handleYearSelect(year)}
          disabled={disabled}
          className={`h-10 rounded-lg text-sm font-inter font-medium transition-colors
            ${disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-[#E5F8F8]"}
            ${active ? "bg-[#00A9A9] text-white hover:bg-[#008F8F]" : ""}
            ${current && !active ? "border border-[#00A9A9] text-[#00A9A9]" : ""}
            ${!active && !current && !disabled ? "text-[#1F1F1F]" : ""}
          `}
        >
          {year}
        </button>
      );
    });

  /* ── Header per view ── */
  const renderHeader = () => {
    if (view === "days") {
      return (
        <>
          <button
            type="button"
            onClick={() => canPrevMonth() && setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
            disabled={!canPrevMonth()}
            className={`p-1 rounded transition ${!canPrevMonth() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1">
            {/* Clickable month */}
            <button
              type="button"
              onClick={() => setView("months")}
              className="px-2 py-1 rounded-md font-semibold text-sm text-gray-900 hover:bg-[#E5F8F8] transition-colors"
            >
              {currentMonth.toLocaleDateString("en-US", { month: "long" })}
            </button>
            {/* Clickable year */}
            <button
              type="button"
              onClick={() => {
                setYearRangeStart(Math.floor(currentMonth.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE);
                setView("years");
              }}
              className="px-2 py-1 rounded-md font-semibold text-sm text-gray-900 hover:bg-[#E5F8F8] transition-colors"
            >
              {currentMonth.getFullYear()}
            </button>
          </div>

          <button
            type="button"
            onClick={() => canNextMonth() && setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
            disabled={!canNextMonth()}
            className={`p-1 rounded transition ${!canNextMonth() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      );
    }

    if (view === "months") {
      return (
        <>
          <button
            type="button"
            onClick={() => canPrevYear() && setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1))}
            disabled={!canPrevYear()}
            className={`p-1 rounded transition ${!canPrevYear() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => {
              setYearRangeStart(Math.floor(currentMonth.getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE);
              setView("years");
            }}
            className="px-2 py-1 rounded-md font-semibold text-sm text-gray-900 hover:bg-[#E5F8F8] transition-colors"
          >
            {currentMonth.getFullYear()}
          </button>

          <button
            type="button"
            onClick={() => canNextYear() && setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1))}
            disabled={!canNextYear()}
            className={`p-1 rounded transition ${!canNextYear() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      );
    }

    /* years view */
    return (
      <>
        <button
          type="button"
          onClick={() => canPrevYearRange() && setYearRangeStart((s) => s - YEARS_PER_PAGE)}
          disabled={!canPrevYearRange()}
          className={`p-1 rounded transition ${!canPrevYearRange() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <span className="font-semibold text-sm text-gray-900">
          {yearRangeStart} – {yearRangeStart + YEARS_PER_PAGE - 1}
        </span>

        <button
          type="button"
          onClick={() => canNextYearRange() && setYearRangeStart((s) => s + YEARS_PER_PAGE)}
          disabled={!canNextYearRange()}
          className={`p-1 rounded transition ${!canNextYearRange() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-72">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {renderHeader()}
      </div>

      {/* Body */}
      {view === "days" && (
        <>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d) => (
              <div key={d} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">{renderDays()}</div>
        </>
      )}

      {view === "months" && (
        <div className="grid grid-cols-3 gap-2">{renderMonths()}</div>
      )}

      {view === "years" && (
        <div className="grid grid-cols-3 gap-2">{renderYears()}</div>
      )}
    </div>
  );
};

export default Calendar;
